import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

const noop = () => { };

function PromoArrowIcon() {
  return (
    <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.5 1.5L9.5 10L1.5 18.5" stroke="black" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export default function MobileMenu({
  isOpen = false,
  onClose = noop,
  children = null,
  cta = null,
  phoneNumber = null,
  phoneHref = null,
  primaryNavigation = [],
  moreNavigation = [],
}) {
  const hasCustomContent = Boolean(children);
  const ctaLabel = cta?.label || cta?.title || "Free Estimate";
  const ctaUrl = cta?.url || "/get-estimate";
  const ctaTitle = cta?.title || ctaLabel;
  const ctaTarget = cta?.newWindow ? "_blank" : undefined;
  const ctaRel = cta?.newWindow ? "noopener noreferrer" : undefined;
  const defaultPhoneNumber = "1-866-379-1669";
  const sanitizePhone = (value) =>
    typeof value === "string" ? value.replace(/[\s().-]/g, "") : null;
  const displayPhoneNumber = phoneNumber || defaultPhoneNumber;
  const displayPhoneHref =
    phoneHref ||
    (sanitizePhone(displayPhoneNumber)
      ? `tel:${sanitizePhone(displayPhoneNumber)}`
      : null);
  const hasPhone = Boolean(displayPhoneNumber && displayPhoneHref);
  const navigationItems = useMemo(
    () => (Array.isArray(primaryNavigation) ? primaryNavigation : []),
    [primaryNavigation]
  );
  const moreNavigationItems = useMemo(
    () => (Array.isArray(moreNavigation) ? moreNavigation : []),
    [moreNavigation]
  );
  const [activePanel, setActivePanel] = useState(null);
  const [renderPanel, setRenderPanel] = useState(null);
  const exitTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const sliderOffset = activePanel ? 100 : 0;
  const getNavItemState = (item) => {
    const rawStyle = item?.navigationLinkStyling;
    const linkStyleValue = typeof rawStyle === "string" ? rawStyle : rawStyle?.value;
    const isSmallUppercase = linkStyleValue === "smallUppercase";
    const isPromoLink = linkStyleValue === "promoLink" || Boolean(item?.navigationPromoLink);
    const promoBody = typeof item?.navigationPromoBody === "string" ? item.navigationPromoBody.trim() : "";

    return { isSmallUppercase, isPromoLink, promoBody };
  };
  const getNavItemClassName = ({ isSmallUppercase, isPromoLink }) =>
    [
      "g-mobile-menu__nav-item",
      isSmallUppercase && "g-mobile-menu__nav-item--small-uppercase",
      isPromoLink && "g-mobile-menu__nav-item--promo-link",
    ]
      .filter(Boolean)
      .join(" ");

  const handleNavBack = () => {
    if (!activePanel) return;
    setActivePanel(null);
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
    }
    exitTimeoutRef.current = window.setTimeout(() => {
      setRenderPanel(null);
      exitTimeoutRef.current = null;
    }, 500);
  };

  const handleNavForward = (item) => {
    if (!item?.children?.length) {
      return;
    }
    const nextPanel = {
      title: item.title,
      url: item.url,
      newWindow: item.newWindow,
      items: item.children,
    };
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }
    setRenderPanel(nextPanel);
    setActivePanel(nextPanel);
  };

  useEffect(() => {
    return () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      return;
    }

    if (!activePanel && !renderPanel) {
      return;
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = window.setTimeout(() => {
      setActivePanel(null);
      setRenderPanel(null);
      closeTimeoutRef.current = null;
    }, 500);
  }, [isOpen, activePanel, renderPanel]);

  return (
    <Transition show={isOpen} as={Fragment} appear>
      <Dialog as="div" className="g-mobile-menu" onClose={onClose} aria-label="Main menu">
        <Transition.Child
          as={Fragment}
          enter="g-mobile-menu__backdrop--enter"
          enterFrom="g-mobile-menu__backdrop--enter-from"
          enterTo="g-mobile-menu__backdrop--enter-to"
          leave="g-mobile-menu__backdrop--leave"
          leaveFrom="g-mobile-menu__backdrop--leave-from"
          leaveTo="g-mobile-menu__backdrop--leave-to"
        >
          <div className="g-mobile-menu__backdrop" aria-hidden="true" />
        </Transition.Child>

        <div className="g-mobile-menu__wrapper">
          <Transition.Child
            as={Fragment}
            enter="g-mobile-menu__panel--enter"
            enterFrom="g-mobile-menu__panel--enter-from"
            enterTo="g-mobile-menu__panel--enter-to"
            leave="g-mobile-menu__panel--leave"
            leaveFrom="g-mobile-menu__panel--leave-from"
            leaveTo="g-mobile-menu__panel--leave-to"
          >
            <Dialog.Panel className="g-mobile-menu__panel">
              <Transition.Child
                as={Fragment}
                enter="g-mobile-menu__panel-inner--enter"
                enterFrom="g-mobile-menu__panel-inner--enter-from"
                enterTo="g-mobile-menu__panel-inner--enter-to"
                leave="g-mobile-menu__panel-inner--leave"
                leaveFrom="g-mobile-menu__panel-inner--leave-from"
                leaveTo="g-mobile-menu__panel-inner--leave-to"
              >
                <div className="g-mobile-menu__panel-inner">
                  <div className="g-mobile-menu__header">
                    <a href="/" className="g-mobile-menu__logo" title="Green Shield Deck Builders">
                      <img src="/assets/logos/horizontal__light.png" alt="Green Shield Deck Builders logo horizontal" />
                      <span>Green Shield Deck Builders</span>
                    </a>

                    <button
                      type="button"
                      className="g-mobile-menu__close"
                      onClick={onClose}
                      aria-label="Close menu"
                    >
                      <span aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                          <line x1="1.12312" y1="0.706956" x2="20.9221" y2="20.5059" stroke="white" strokeWidth="2" />
                          <line y1="-1" x2="28" y2="-1" transform="matrix(-0.707107 0.707107 0.707107 0.707107 21.2129 1.41406)" stroke="white" strokeWidth="2" />
                        </svg>
                      </span>
                    </button>
                  </div>

                  <div className="g-mobile-menu__scroll">
                    {hasCustomContent ? (
                      children
                    ) : (
                      <div className="g-mobile-menu__nav">
                        <div
                          className="g-mobile-menu__nav-slider"
                          style={{ transform: `translateX(-${sliderOffset}%)` }}
                        >
                          <div className="g-mobile-menu__nav-panel g-mobile-menu__nav-panel--root">
                            <ul className="g-mobile-menu__nav-list">
                              {navigationItems.map((item, index) => {
                                const hasChildren = item?.children?.length;
                                const linkTarget = item?.newWindow ? "_blank" : undefined;
                                const linkRel = item?.newWindow ? "noopener noreferrer" : undefined;
                                const navState = getNavItemState(item);
                                const isPassive = (item?.tag === "span" || !item?.url) && !hasChildren && !navState.isSmallUppercase && !navState.isPromoLink;

                                if (isPassive) {
                                  return null;
                                }

                                return (
                                  <li key={`${item?.title || "item"}-${index}`} className={getNavItemClassName(navState)}>
                                    {hasChildren ? (
                                      <button
                                        type="button"
                                        onClick={() => handleNavForward(item)}
                                      >
                                        <span>{item?.title || "Menu item"}</span>
                                        <span aria-hidden="true" className="g-mobile-menu__nav-arrow">
                                          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
                                            <path d="M0.72863 0.683595L9.19922 9.68359L0.728632 18.6836" stroke="#C4F489" strokeWidth="2" />
                                          </svg>
                                        </span>
                                      </button>
                                    ) : (
                                      <a
                                        href={item?.url || "#"}
                                        target={linkTarget}
                                        rel={linkRel}
                                      >
                                        <span>{item?.title || "Menu item"}</span>
                                      </a>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>

                            {moreNavigationItems.length ? (
                              <>
                                <ul className="g-mobile-menu__nav-list g-mobile-menu__nav-list--more">
                                  {moreNavigationItems.map((item, index) => {
                                    const linkTarget = item?.newWindow ? "_blank" : undefined;
                                    const linkRel = item?.newWindow ? "noopener noreferrer" : undefined;

                                    const navState = getNavItemState(item);
                                    const isPassive = (item?.tag === "span" || !item?.url) && !navState.isSmallUppercase && !navState.isPromoLink;

                                    if (isPassive) {
                                      return null;
                                    }

                                    return (
                                      <li key={`more-${item?.title || "item"}-${index}`} className={getNavItemClassName(navState)}>
                                        <a
                                          href={item?.url || "#"}
                                          target={linkTarget}
                                          rel={linkRel}
                                        >
                                          {navState.isPromoLink ? (
                                            <>
                                              <span className="g-mobile-menu__promo-text">
                                                <span className="g-mobile-menu__promo-heading">{item?.title || "Menu item"}</span>
                                                {navState.promoBody !== "" && (
                                                  <span className="g-mobile-menu__promo-body">{navState.promoBody}</span>
                                                )}
                                              </span>
                                              <PromoArrowIcon />
                                            </>
                                          ) : (
                                            <span>{item?.title || "Menu item"}</span>
                                          )}
                                        </a>
                                      </li>
                                    );
                                  })}
                                </ul>

                              </>
                            ) : null}
                          </div>

                          {renderPanel ? (
                            <div className="g-mobile-menu__nav-panel">
                              <button type="button" className="g-mobile-menu__nav-back" onClick={handleNavBack}>
                                <span aria-hidden="true" className="g-mobile-menu__nav-back-arrow">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                                    <line y1="-0.5" x2="15.2727" y2="-0.5" transform="matrix(-1 0 0 1 16.7246 8.36328)" stroke="#E9E9E6" />
                                    <path d="M8.72461 0.363281L0.724609 7.99964L8.72461 15.636" stroke="#E9E9E6" />
                                  </svg>
                                </span>
                                <span>{renderPanel?.title || "Menu"}</span>
                              </button>
                              <ul className="g-mobile-menu__nav-list">
                                {(renderPanel?.items || []).map((item, childIndex) => {
                                  const linkTarget = item?.newWindow ? "_blank" : undefined;
                                  const linkRel = item?.newWindow ? "noopener noreferrer" : undefined;
                                  const navState = getNavItemState(item);
                                  const isPassive = (item?.tag === "span" || !item?.url) && !navState.isSmallUppercase && !navState.isPromoLink;

                                  if (isPassive) {
                                    return null;
                                  }

                                  return (
                                    <li key={`${item?.title || "item"}-${childIndex}`} className={getNavItemClassName(navState)}>
                                      <a
                                        href={item?.url || "#"}
                                        target={linkTarget}
                                        rel={linkRel}
                                      >
                                        {navState.isPromoLink ? (
                                          <>
                                            <span className="g-mobile-menu__promo-text">
                                              <span className="g-mobile-menu__promo-heading">{item?.title || "Menu item"}</span>
                                              {navState.promoBody !== "" && (
                                                <span className="g-mobile-menu__promo-body">{navState.promoBody}</span>
                                              )}
                                            </span>
                                            <PromoArrowIcon />
                                          </>
                                        ) : (
                                          <span>{item?.title || "Menu item"}</span>
                                        )}
                                      </a>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="g-mobile-menu__footer">
                    <div className="g-mobile-menu__footer-cta">
                      <div className="e-button__special-button-wrapper">
                      <img src="/assets/accents/green-corner-blurred-glow--rounded.png" alt="" />
                        <a
                            className="e-button e-button--special"
                            href={ctaUrl}
                            title={ctaTitle}
                            target={ctaTarget}
                            rel={ctaRel}
                        >
                            <span>{ctaLabel}</span>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <mask id="mask0_5520_1563" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                                <rect width="24" height="24" fill="#D9D9D9"></rect>
                            </mask>
                            <g mask="url(#mask0_5520_1563)">
                                <path d="M16.175 13H4V11H16.175L10.575 5.4L12 4L20 12L12 20L10.575 18.6L16.175 13Z" fill="#1C1B1F"></path>
                            </g>
                            </svg>
                        </a>
                      </div>
                      {hasPhone ? (
                        <span className="g-mobile-menu__footer-phone">
                          <span className="g-mobile-menu__footer-phone-pre-text">
                            Or, give us a call!
                          </span>
                          <a href={displayPhoneHref}>{displayPhoneNumber}</a>
                        </span>
                      ) : null}
                    </div>
                    <div className="g-mobile-menu__footer-icons">
                      <ul className="g-mobile-menu__footer-icons-items">
                        <li>
                          <svg xmlns="http://www.w3.org/2000/svg" width="35" height="30" fill="none" viewBox="0 0 35 30"><path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="1.094" d="m12.432 16.972 2.789 2.625 7.656-7.657c.82-.71 2.351.493 1.312 1.641l-7.656 8.203c-.71.602-1.75.547-2.406-.11l-3.172-3.062c-1.203-1.367.547-2.57 1.477-1.64M9.367 7.892s3.5-3.171 1.75-6.89c-.055-.055-4.484 1.094-1.75 6.89M6.415 12.486s1.477-3.992-2.68-6.562c0 0-2.625 3.828 2.68 6.562M6.083 18.94s-.711-4.102-5.086-5.087c.054 0-1.04 4.758 5.086 5.086M8.986 24.136s-3.227-3.117-7.11-1.586c-.054.055 1.532 4.047 7.11 1.586M13.47 27.089s-3.5-1.313-6.946.765c-.055 0 3.172 3.39 6.945-.765" /><path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="1.094" d="M21.125 5.377c4.484 1.586 7.656 5.852 7.656 10.828 0 6.344-5.14 11.485-11.484 11.485S5.813 22.548 5.813 16.205c0-5.031 3.171-9.242 7.656-10.828" /><path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="1.094" d="M25.224 7.892s-3.5-3.171-1.75-6.89c.055-.055 4.485 1.094 1.75 6.89M28.177 12.486s-1.477-3.992 2.68-6.562c0 0 2.624 3.828-2.68 6.562M28.508 18.94s.71-4.102 5.086-5.087c-.055 0 1.039 4.758-5.086 5.086M25.61 24.136s3.226-3.117 7.109-1.586c.054.055-1.532 4.047-7.11 1.586M21.125 27.089s3.5-1.313 6.945.765c.055 0-3.172 3.39-6.945-.765" /></svg>
                          <span>Best in industry warranty</span>
                        </li>
                        <li>
                          <svg xmlns="http://www.w3.org/2000/svg" width="33" height="27" fill="none" viewBox="0 0 33 27"><path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="1.094" d="M16.148 20.96h-4.921v5.196l-4.649-5.14h-2.68C2.312 21.016 1 19.758 1 18.172V3.953C1 2.313 2.313 1 3.898 1h25.157a2.92 2.92 0 0 1 2.898 2.898v14.22c0 1.585-1.312 2.843-2.898 2.843h-.274" /><path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="1.094" d="M19.047 24.844h-2.133a.75.75 0 0 1-.766-.766v-7.055c0-.437.329-.765.766-.765h2.133c.437 0 .765.328.765.765v7.055c.055.438-.328.766-.765.766" /><path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="1.094" d="M19.813 17.024h2.406l2.57-3.282c.164-.164.383-.218.602-.109.601.383 1.64 1.367-.164 3.445h3.171c.766 0 1.313.766 1.149 1.477l-1.04 3.719c-.327 1.093-1.312 1.86-2.46 1.86h-6.235M28.234 5.484H14.398M21.29 8.656h-5.688M23.86 8.656h4.374M15.602 12.156h7.437M8.492 15.055a4.867 4.867 0 1 0 0-9.735 4.867 4.867 0 0 0 0 9.735" /><path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="1.094" d="m6.469 10.078 1.422 1.64 3.062-3.554" /></svg>
                          <span>11,000+ Happy customers</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Transition.Child>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
